// /src/WhatsAppManager.js
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "./services/api";

const WhatsAppManager = () => {
  const [statusData, setStatusData] = useState({
    status: "INITIALIZING",
    qrCodeUrl: null,
    connectedUser: null,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [testMobile, setTestMobile] = useState("");
  const [testSuccess, setTestSuccess] = useState("");
  const [testError, setTestError] = useState("");

  const fetchStatus = async () => {
    try {
      const res = await api.get("/whatsapp/status");
      if (res.data) {
        setStatusData(res.data);
      }
    } catch (err) {
      console.error("Error fetching WhatsApp status:", err);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleDisconnect = async () => {
    if (!window.confirm("Are you sure you want to unlink this WhatsApp device?")) return;
    try {
      setActionLoading(true);
      await api.post("/whatsapp/disconnect");
      await fetchStatus();
    } catch (err) {
      console.error(err);
      alert("Failed to unlink device.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReconnect = async () => {
    try {
      setActionLoading(true);
      await api.post("/whatsapp/reconnect");
      await fetchStatus();
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendTestMessage = async () => {
    if (!testMobile.trim()) return;
    try {
      setTestSuccess("");
      setTestError("");
      setActionLoading(true);

      await api.post("/whatsapp/send-quotation", {
        mobile: testMobile.trim(),
        pdfBase64: "JVBERi0xLjQKJcTl8uXrCjEgMCBvYmoKPDwKL1R5cGUgL0NhdGFsb2cKL1BhZ2VzIDIgMCBSCj4+CmVuZG9iagoyIDAgb2JqCjw8Ci9UeXBlIC9QYWdlcwovS2lkcyBbMyAwIFJdCi9Db3VudCAxCj4+CmVuZG9iagozIDAgb2JqCjw8Ci9UeXBlIC9QYWdlCi9QYXJlbnQgMiAwIFIKL01lZGlhQm94IFswIDAgNjEyIDc5Ml0KPj4KZW5kb2JqCnRyYWlsZXIKPDwKL1Jvb3QgMSAwIFIKPj4KJSVFT0YK", // valid minimal pdf
        quotationNo: "TEST-001",
        customerName: "Avantica Test",
        grandTotal: 100,
      });

      setTestSuccess(`Test message and PDF delivered to ${testMobile}!`);
      setTestMobile("");
    } catch (err) {
      setTestError(err.response?.data?.error || "Failed to send test message.");
    } finally {
      setActionLoading(false);
    }
  };

  const isConnected = statusData.status === "CONNECTED";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 text-white overflow-x-hidden pb-20 pt-8">
      {/* Background Orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="orb absolute w-[500px] h-[500px] -top-20 -left-20 bg-emerald-600/15 rounded-full blur-[120px]"></div>
        <div className="orb absolute w-[400px] h-[400px] bottom-0 right-0 bg-purple-600/15 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-4 max-w-4xl relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-3xl bg-gradient-to-tr from-[#25D366] to-emerald-400 text-white text-3xl shadow-xl shadow-green-900/30 mb-4 animate-fade-in-up">
            <i className="fab fa-whatsapp"></i>
          </div>
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-200 via-white to-purple-200">
            WhatsApp Integration
          </h1>
          <p className="text-purple-300/60 mt-2 text-sm uppercase tracking-widest font-medium">
            Direct PDF Quotation Delivery
          </p>
        </div>

        {/* Status Card */}
        <div className="glass-panel p-8 rounded-3xl border border-white/10 shadow-2xl backdrop-blur-xl bg-white/5 relative overflow-hidden mb-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-6 border-b border-white/10">
            <div className="flex items-center gap-4">
              <div className={`w-4 h-4 rounded-full ${isConnected ? "bg-emerald-400 shadow-lg shadow-emerald-400/50 animate-pulse" : "bg-amber-400 shadow-lg shadow-amber-400/50 animate-ping"}`}></div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  Status: {isConnected ? "🟢 Connected" : statusData.status === "SCAN_QR" ? "🟡 Waiting for Scan" : "⚪ Initializing..."}
                </h2>
                <p className="text-xs text-white/50 mt-0.5">
                  {isConnected
                    ? `Linked to device: +${statusData.connectedUser?.phone || "Connected"}`
                    : "Scan the QR code below to connect your company WhatsApp"}
                </p>
              </div>
            </div>

            {isConnected ? (
              <button
                onClick={handleDisconnect}
                disabled={actionLoading}
                className="px-5 py-2.5 rounded-xl font-bold text-sm bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-300 transition-all flex items-center gap-2"
              >
                <i className="fas fa-unlink"></i> Unlink Device
              </button>
            ) : (
              <button
                onClick={handleReconnect}
                disabled={actionLoading}
                className="px-5 py-2.5 rounded-xl font-bold text-sm bg-white/10 hover:bg-white/15 border border-white/10 text-white transition-all flex items-center gap-2"
              >
                <i className="fas fa-sync-alt"></i> Refresh QR
              </button>
            )}
          </div>

          {/* QR Code Scan Area */}
          {!isConnected && (
            <div className="pt-8 flex flex-col md:flex-row items-center justify-center gap-10">
              {/* QR Image Box */}
              <div className="bg-white p-4 rounded-3xl shadow-2xl shadow-emerald-950/40 border-4 border-white/20 relative flex items-center justify-center min-w-[260px] min-h-[260px]">
                {statusData.qrCodeUrl ? (
                  <img
                    src={statusData.qrCodeUrl}
                    alt="WhatsApp QR Code"
                    className="w-64 h-64 rounded-xl"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center text-slate-700 py-12">
                    <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3"></div>
                    <span className="text-xs font-bold text-slate-500">Generating QR Code...</span>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="max-w-md space-y-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <i className="fas fa-qrcode text-emerald-400"></i> How to connect:
                </h3>
                <ol className="space-y-3 text-sm text-purple-200/80 list-decimal list-inside leading-relaxed font-medium">
                  <li>Open <span className="text-white font-bold">WhatsApp</span> on your phone</li>
                  <li>Tap <span className="text-white font-bold">Settings</span> (iOS) or <span className="text-white font-bold">3 Dots ⋮</span> (Android)</li>
                  <li>Tap <span className="text-white font-bold">Linked Devices</span> ➔ <span className="text-white font-bold">Link a Device</span></li>
                  <li>Point your phone camera at this QR Code to scan</li>
                </ol>
                <p className="text-xs text-emerald-300/60 pt-2 flex items-center gap-1.5">
                  <i className="fas fa-shield-alt"></i> Safe and secure via official WhatsApp Multi-Device connection
                </p>
              </div>
            </div>
          )}

          {/* Connected Device Details */}
          {isConnected && (
            <div className="pt-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white/5 p-5 rounded-2xl border border-white/5">
                  <p className="text-xs text-purple-300 uppercase font-bold tracking-wider mb-1">Connected WhatsApp Number</p>
                  <p className="text-xl font-mono text-emerald-400 font-bold">+{statusData.connectedUser?.phone || "Connected"}</p>
                </div>
                <div className="bg-white/5 p-5 rounded-2xl border border-white/5">
                  <p className="text-xs text-purple-300 uppercase font-bold tracking-wider mb-1">Device Name</p>
                  <p className="text-xl text-white font-bold">{statusData.connectedUser?.name || "Avantica Business"}</p>
                </div>
              </div>

              {/* Test Message Box */}
              <div className="bg-slate-900/60 p-6 rounded-2xl border border-white/10 space-y-4">
                <h4 className="text-sm font-bold text-white flex items-center gap-2">
                  <i className="fas fa-paper-plane text-emerald-400"></i> Send a Test PDF & Message
                </h4>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={testMobile}
                    onChange={(e) => setTestMobile(e.target.value.replace(/\D/g, ""))}
                    placeholder="Enter 10-digit mobile number"
                    className="flex-1 bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/50 font-mono"
                  />
                  <button
                    onClick={handleSendTestMessage}
                    disabled={actionLoading || !testMobile.trim()}
                    className={`px-6 py-3 rounded-xl font-bold text-sm transition-all flex items-center gap-2 ${
                      actionLoading || !testMobile.trim()
                        ? "bg-white/10 text-white/30 cursor-not-allowed"
                        : "bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-900/30"
                    }`}
                  >
                    {actionLoading ? "Sending..." : "Send Test"}
                  </button>
                </div>

                {testSuccess && (
                  <p className="text-xs text-emerald-300 font-bold flex items-center gap-1.5">
                    <i className="fas fa-check-circle"></i> {testSuccess}
                  </p>
                )}
                {testError && (
                  <p className="text-xs text-red-300 font-bold flex items-center gap-1.5">
                    <i className="fas fa-exclamation-triangle"></i> {testError}
                  </p>
                )}
              </div>

              {/* Quotation Follow-Ups Banner */}
              <div className="bg-gradient-to-r from-purple-900/40 via-indigo-900/40 to-slate-900/60 p-6 rounded-2xl border border-purple-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="text-base font-bold text-white flex items-center gap-2">
                    <span>⏰</span> Follow Up with Customers
                  </h4>
                  <p className="text-xs text-purple-200/70 mt-1 max-w-xl">
                    Send polite, bilingual (ગુજરાતી + English) WhatsApp follow-up reminders to customers who received quotations.
                  </p>
                </div>
                <Link
                  to="/followups"
                  className="px-5 py-3 rounded-xl font-bold text-sm bg-amber-500 hover:bg-amber-400 text-slate-950 shadow-lg shadow-amber-950/40 transition-all flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fas fa-bell"></i>
                  <span>Open Follow-Up Center</span>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppManager;
